import chains from '@safe-global/utils/config/chains'
import { getSafeL2SingletonDeployments, getSafeSingletonDeployments } from '@safe-global/safe-deployments'
import ExternalStore from '@safe-global/utils/services/ExternalStore'
import { Gnosis_safe__factory } from '@safe-global/utils/types/contracts'
import Safe, { type ContractNetworksConfig } from '@safe-global/protocol-kit'
import { isValidMasterCopy } from '@safe-global/utils/services/contracts/safeContracts'
import { isPredictedSafeProps, isReplayedSafeProps } from '@/features/counterfactual/services'
import { isLegacyVersion } from '@safe-global/utils/services/contracts/utils'
import { isInDeployments } from '@safe-global/utils/hooks/coreSDK/utils'
import { getCanonicalMultiSendContractNetworks } from '@safe-global/utils/hooks/coreSDK/contractNetworks'
import type { SafeCoreSDKProps } from '@safe-global/utils/hooks/coreSDK/types'
import { keccak256 } from 'ethers'
import {
  getL2MasterCopyVersionByCodeHash,
  isL2MasterCopyCodeHash,
} from '@safe-global/utils/services/contracts/deployments'
import { logError, Errors } from '@/services/exceptions'

// Safe Core SDK
export const initSafeSDK = async ({
  provider,
  chainId,
  address,
  version,
  implementationVersionState,
  implementation,
  undeployedSafe,
}: SafeCoreSDKProps): Promise<Safe | undefined> => {
  const providerNetwork = (await provider.getNetwork()).chainId
  if (providerNetwork !== BigInt(chainId)) {
    return
  }

  let safeVersion = version ?? (await Gnosis_safe__factory.connect(address, provider).VERSION())
  let isL1SafeSingleton = chainId === chains.eth
  let contractNetworks: ContractNetworksConfig | undefined

  // If it is an official deployment we should still initiate the safeSDK
  if (!isValidMasterCopy(implementationVersionState)) {
    const masterCopy = implementation

    const safeL1Deployment = getSafeSingletonDeployments({ network: chainId, version: safeVersion })
    const safeL2Deployment = getSafeL2SingletonDeployments({ network: chainId, version: safeVersion })

    // safe-deployments v2 stores deployment TYPE keys (e.g. "canonical") in
    // networkAddresses[chainId], not addresses. Resolve them to real addresses
    // via `deployments` before the membership check, otherwise a canonical
    // master copy is never recognized -> the SDK falls into the custom-network
    // path with an incomplete contractNetworks -> "Invalid multiSend contract
    // address" for any Safe not yet indexed on the chain.
    const resolveDeploymentAddresses = (deployment: ReturnType<typeof getSafeL2SingletonDeployments>): string[] => {
      const types = deployment?.networkAddresses[chainId]
      const typeList = Array.isArray(types) ? types : types ? [types] : []
      const deployments = deployment?.deployments as Record<string, { address: string } | undefined> | undefined
      return typeList
        .map((type) => deployments?.[type]?.address)
        .filter((address): address is string => Boolean(address))
    }

    isL1SafeSingleton = isInDeployments(masterCopy, resolveDeploymentAddresses(safeL1Deployment))
    const isL2SafeMasterCopy = isInDeployments(masterCopy, resolveDeploymentAddresses(safeL2Deployment))

    if (!isL1SafeSingleton && !isL2SafeMasterCopy) {
      try {
        const code = await provider.getCode(masterCopy)

        if (!code || code === '0x') {
          console.warn(`[SafeSDK] No bytecode found for mastercopy at ${masterCopy}`)
          return
        }

        const codeHash = keccak256(code)
        const isUpgradeableL2MasterCopy = isL2MasterCopyCodeHash(codeHash)

        if (!isUpgradeableL2MasterCopy) {
          console.warn(`[SafeSDK] Mastercopy at ${masterCopy} is not a recognized L2 mastercopy`)
          return
        }

        const upgradeableVersion = getL2MasterCopyVersionByCodeHash(codeHash)

        if (!upgradeableVersion) {
          console.warn(`[SafeSDK] Could not determine version for L2 mastercopy at ${masterCopy}`)
          return
        }

        // Use the custom mastercopy address with the SDK
        contractNetworks = {
          [chainId]: {
            safeSingletonAddress: masterCopy,
          },
        }

        safeVersion = upgradeableVersion
        isL1SafeSingleton = false
      } catch (error) {
        logError(Errors._808, error)
        return
      }
    }

    if (isL2SafeMasterCopy) {
      isL1SafeSingleton = false
    }
  }
  // Legacy Safe contracts
  if (isLegacyVersion(safeVersion)) {
    isL1SafeSingleton = true
  }

  contractNetworks = getCanonicalMultiSendContractNetworks({
    implementationAddress: implementation,
    chainId,
    safeVersion,
    contractNetworks,
  })

  if (undeployedSafe) {
    if (isPredictedSafeProps(undeployedSafe.props) || isReplayedSafeProps(undeployedSafe.props)) {
      // Replayed Safes keep `safeVersion` at the top level, but protocol-kit reads the
      // version from `safeDeploymentConfig.safeVersion`. When it's missing protocol-kit
      // falls back to DEFAULT_SAFE_VERSION (1.3.0) and resolves 1.3.0 contracts, which
      // are not deployed/registered on chains that only ship 1.4.1 (e.g. Humanity
      // mainnet) -> "Invalid multiSend contract address". Map replayed props to a proper
      // predictedSafe so the real Safe version is used.
      const predictedSafe = isReplayedSafeProps(undeployedSafe.props)
        ? {
            safeAccountConfig: undeployedSafe.props.safeAccountConfig,
            safeDeploymentConfig: {
              saltNonce: undeployedSafe.props.saltNonce,
              safeVersion: undeployedSafe.props.safeVersion,
            },
          }
        : undeployedSafe.props

      return Safe.init({
        provider: provider._getConnection().url,
        isL1SafeSingleton,
        ...(contractNetworks ? { contractNetworks } : {}),
        predictedSafe,
      })
    }

    return
  }

  return Safe.init({
    provider: provider._getConnection().url,
    safeAddress: address,
    isL1SafeSingleton,
    ...(contractNetworks ? { contractNetworks } : {}),
  })
}

export const {
  getStore: getSafeSDK,
  setStore: setSafeSDK,
  useStore: useSafeSDK,
} = new ExternalStore<Safe | undefined>()
